import React from 'react';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import '../../../component/ReuseableComponent/ReuseableComponent.css';
import Modal from '@mui/material/Modal';
import '../../../component/ToolBox/ToolBox.css';
import '../Order.css';
import { CmpButtonWithIcon } from '../../../component/ToolBox/ToolBox';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';

const fDummyFunction = () => {};
const fmt = (v, d = 2) => (v != null ? Number(v).toFixed(d) : '0.00');

// --- Color-coded Chip ---
const CChip = ({ label, value, bg = '#e8f0fe', color = '#1a56db', border = '#c3d9f7' }) => (
  <Chip
    size="small"
    label={
      <span>
        <span style={{ fontSize: '0.62rem', color: '#555', marginRight: '4px' }}>{label}</span>
        <span style={{ fontWeight: 700, fontSize: '0.78rem', color }}>{value}</span>
      </span>
    }
    sx={{
      height: '26px',
      borderRadius: '5px',
      backgroundColor: bg,
      border: `1px solid ${border}`,
      '& .MuiChip-label': { px: 0.8, py: 0 }
    }}
  />
);

// Color presets — white bg, vibrant text + border
const chipCost = { bg: '#fff', color: '#cf1322', border: '#ffa39e' }; // coral-red: cost fields
const chipStock = { bg: '#fff', color: '#006d75', border: '#5cdbd3' }; // teal: stock
const chipAvg = { bg: '#fff', color: '#ad6800', border: '#ffd591' }; // orange: avg cost
const chipMargin = { bg: '#fff', color: '#722ed1', border: '#d3adf7' }; // purple: margin
const chipTSR = { bg: '#fff', color: '#0050b3', border: '#91d5ff' }; // sky blue: TSR cost
const chipGPGreen = { bg: '#fff', color: '#237804', border: '#b7eb8f' }; // lime green: GP+
const chipGPRed = { bg: '#fff', color: '#a8071a', border: '#ff7875' }; // red: GP-

// --- Single Item Card ---
const MarginCard = ({ row, index, isPosted, onMarginChange, vMobile }) => {
  const gpColor = Number(row.GP) >= 0 ? chipGPGreen : chipGPRed;

  return (
    <Box
      sx={{
        mx: 0.5,
        mb: 2.16,
        borderRadius: '8px',
        borderLeft: `3px solid ${['#722ed1', '#13c2c2', '#fa8c16', '#eb2f96', '#52c41a', '#1890ff', '#faad14', '#f5222d'][index % 8]}`,
        border: '1px solid #e8e8e8',
        borderLeftWidth: '3px',
        borderLeftColor: [
          '#722ed1',
          '#13c2c2',
          '#fa8c16',
          '#eb2f96',
          '#52c41a',
          '#1890ff',
          '#faad14',
          '#f5222d'
        ][index % 8],
        backgroundColor: '#fff',
        boxShadow: '0 2px 5px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        '&:hover': { boxShadow: '0 4px 14px rgba(0,0,0,0.1)', borderColor: '#bbb' }
      }}>
      {/* HEADER: Item + Vendor + Qty */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          backgroundColor: '#fff',
          borderBottom: '1.5px solid #cacaca'
        }}>
        {/* LEFT: Item Name */}
        <Box
          sx={{
            fontWeight: 700,
            fontSize: '0.82rem',
            color: '#333',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1
          }}>
          {row.ItmName}
        </Box>
        {/* CENTER: Vendor */}
        {!vMobile && row.TRNS_PARTYName && (
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Chip
              size="small"
              label={row.TRNS_PARTYName}
              sx={{
                height: '22px',
                fontSize: '0.65rem',
                fontWeight: 500,
                backgroundColor: '#fff',
                border: '1px solid #d1d9e0',
                maxWidth: '300px',
                '& .MuiChip-label': { px: 0.6, overflow: 'hidden', textOverflow: 'ellipsis' }
              }}
            />
          </Box>
        )}
        {/* RIGHT: Qty */}
        <Chip
          size="small"
          label={`${fmt(row.TRNS_QTY, 0)} x ${row.TRNS_UNITName}`}
          sx={{
            height: '22px',
            fontSize: '0.65rem',
            fontWeight: 700,
            backgroundColor: '#13c2c2',
            color: '#fff',
            border: 'none',
            flexShrink: 0,
            '& .MuiChip-label': { px: 0.6 }
          }}
        />
      </Box>

      {/* BODY: Chips grid */}
      <Box
        sx={{
          px: 0.6,
          py: '10px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '9px',
          alignItems: 'center'
        }}>
        {/* Cost section */}
        <CChip label="Rate" value={fmt(row.TRNS_UnitRate)} {...chipCost} />
        {!vMobile && <CChip label="Net" value={fmt(row.TRNS_NetAmt)} {...chipCost} />}
        <CChip label="Landed" value={fmt(row.LandedCost)} {...chipCost} />

        {/* Stock section */}
        <CChip label="DC Stk" value={fmt(row.DCStock, 0)} {...chipStock} />
        <CChip label="Avg" value={fmt(row.AvgCost)} {...chipAvg} />

        {/* Min margin */}
        {!vMobile && <CChip label="Min" value={fmt(row.TRNS_Add_Margin_Min)} {...chipMargin} />}

        {/* Divider dot */}
        <Box
          sx={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#d3adf7' }}
        />

        {/* Editable margin */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
          <Box sx={{ fontSize: '0.62rem', color: '#555', whiteSpace: 'nowrap', fontWeight: 500 }}>
            Margin
          </Box>
          {isPosted ? (
            <Chip
              size="small"
              label={fmt(row.TRNS_Add_Margin_CTN)}
              sx={{
                height: '22px',
                fontWeight: 700,
                fontSize: '0.68rem',
                borderRadius: '4px',
                ...chipMargin,
                backgroundColor: chipMargin.bg,
                border: `1px solid ${chipMargin.border}`,
                color: chipMargin.color,
                '& .MuiChip-label': { px: 0.6 }
              }}
            />
          ) : (
            <TextField
              variant="outlined"
              size="small"
              type="number"
              value={row.TRNS_Add_Margin_CTN ?? ''}
              onChange={(e) => onMarginChange(e.target.value, row.id)}
              sx={{
                width: '85px',
                '& .MuiOutlinedInput-root': {
                  height: '26px',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  '& fieldset': { borderColor: '#0d73bb' },
                  '&.Mui-focused fieldset': { borderColor: '#0a5a94', borderWidth: '1.5px' },
                  '&:hover fieldset': { borderColor: '#1a8fd8' },
                  color: '#0d73bb'
                },
                '& input': { textAlign: 'right', padding: '1px 4px' }
              }}
            />
          )}
        </Box>

        {/* Results */}
        <CChip label="Net" value={fmt(row.TRNS_Add_Margin_Net)} {...chipMargin} />
        <CChip label="TSR" value={fmt(row.TRNS_TSR_RSP)} {...chipTSR} />
        <CChip label="GP" value={`${fmt(row.GP)}%`} {...gpColor} />
      </Box>
    </Box>
  );
};

const MarginPostingByOrder = ({
  xOpen = false,
  xTitle = '',
  fCloseModal,
  xOrderInfo = {},
  xLoadDetailRow = [],
  xfetchPost = () => {},
  setLoadDetailRow
}) => {
  const vMobile = useMediaQuery('(max-width:650px)');

  const isPosted = xOrderInfo.marginStatus === 'POST';

  const onMarginChange = (value, rowId) => {
    const val = Number(value) || 0;
    const updatedList = xLoadDetailRow.map((i) =>
      i.id === rowId
        ? {
            ...i,
            TRNS_Add_Margin_CTN: val,
            TRNS_Add_Margin_Net: i.TRNS_QTY * val,
            TRNS_TSR_RSP: i.TRNS_UnitRate + val
          }
        : i
    );
    setLoadDetailRow(updatedList);
  };

  const netMarginTotal = xLoadDetailRow
    .reduce((sum, r) => sum + (Number(r.TRNS_Add_Margin_Net) || 0), 0)
    .toFixed(2);

  return (
    <Modal
      open={xOpen}
      onClose={fCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '8px',
          fontFamily: 'Montserrat, Lato, sans-serif'
        }}
        className="model-gc-search">
        {/* [A] HEADER */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 1.5,
            background: 'linear-gradient(135deg, #0a5a94 0%, #0d73bb 50%, #1a8fd8 100%)',
            color: '#fff',
            borderRadius: '8px 8px 0 0',
            fontSize: '14px',
            fontWeight: 600
          }}>
          <span>{xTitle}</span>
          <CloseIcon
            onClick={!!fCloseModal ? fCloseModal : fDummyFunction}
            sx={{ cursor: 'pointer', fontSize: '18px', '&:hover': { opacity: 0.7 } }}
          />
        </Box>

        {/* [B] INFO BAR */}
        <Box
          sx={{
            mx: 0.5,
            mt: 0.4,
            p: 0.8,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: '#f9f0ff',
            borderRadius: '4px',
            fontSize: '0.7rem'
          }}>
          <Chip
            size="small"
            label={`Order: ${xOrderInfo.orderId}`}
            sx={{
              height: '26px',
              fontSize: '0.65rem',
              fontWeight: 600,
              p: 1,
              backgroundColor: '#fff7e6',
              border: '1px solid #ffd591',
              color: '#ad6800',
              '& .MuiChip-label': { px: 0.7 }
            }}
          />
          <Chip
            size="small"
            label={`Date: ${xOrderInfo.orderDate ? moment(xOrderInfo.orderDate).format('DD/MM/YYYY') : ''}`}
            sx={{
              height: '26px',
              fontSize: '0.65rem',
              fontWeight: 500,
              p: 1,
              backgroundColor: '#e6fffb',
              border: '1px solid #5cdbd3',
              '& .MuiChip-label': { px: 0.7 }
            }}
          />
          <Chip
            size="small"
            label={xOrderInfo.marginStatus || 'ENTER'}
            sx={{
              height: '26px',
              fontSize: '0.65rem',
              fontWeight: 700,
              p: 1,
              color: '#fff',
              backgroundColor: isPosted ? '#019F4B' : '#722ed1',
              border: 'none',
              '& .MuiChip-label': { px: 0.7 }
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Chip
            size="small"
            label={`${xLoadDetailRow.length} Items`}
            sx={{
              height: '26px',
              fontSize: '0.65rem',
              fontWeight: 600,
              p: 1,
              backgroundColor: '#fff1f0',
              border: '1px solid #ffa39e',
              color: '#cf1322',
              '& .MuiChip-label': { px: 0.7 }
            }}
          />
        </Box>

        {/* [C] CARDS */}
        <Box
          sx={{
            mx: 0.3,
            mt: 0.6,
            flexGrow: 1,
            overflowY: 'auto',
            overflowX: 'hidden'
          }}>
          {xLoadDetailRow.map((row, index) => (
            <React.Fragment key={row.id}>
              <MarginCard
                row={row}
                index={index}
                isPosted={isPosted}
                onMarginChange={onMarginChange}
                vMobile={vMobile}
              />
              {index < xLoadDetailRow.length - 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', mx: 2, my: 0.3 }}>
                  <Box
                    sx={{
                      flex: 1,
                      height: '2px',
                      background:
                        'linear-gradient(to right, transparent 0%, #722ed1 15%, #13c2c2 40%, #fa8c16 60%, #eb2f96 85%, transparent 100%)',
                      borderRadius: '1px'
                    }}
                  />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>

        {/* [D] FOOTER */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 0.8,
            borderTop: '2px solid #722ed1',
            backgroundColor: '#f9f0ff',
            borderRadius: '0 0 8px 8px',
            '& .mui-btn-wt-icon': { width: 'auto', minWidth: '65px' }
          }}>
          <Chip
            size="small"
            label={
              <span>
                <span style={{ fontSize: '0.65rem', color: '#531dab', fontWeight: 600 }}>
                  Net Margin:{' '}
                </span>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#722ed1' }}>
                  {netMarginTotal}
                </span>
              </span>
            }
            sx={{
              height: '24px',
              backgroundColor: '#f9f0ff',
              border: '1px solid #d3adf7',
              borderRadius: '6px',
              '& .MuiChip-label': { px: 0.8 }
            }}
          />
          <Box sx={{ display: 'flex', gap: 0.6 }}>
            <CmpButtonWithIcon
              xvariant={'contained'}
              xsize={'small'}
              xonClick={() => xfetchPost(false)}
              xLabel={'Save'}
              xIcon={''}
              xDisable={xLoadDetailRow.length === 0 || isPosted}
            />
            <CmpButtonWithIcon
              xvariant={'contained'}
              xColor={'success'}
              xsize={'small'}
              xonClick={() => xfetchPost(true)}
              xLabel={'Post'}
              xIcon={''}
              xDisable={xLoadDetailRow.length === 0 || isPosted}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default MarginPostingByOrder;
